import { Component, ContentChild, Input, TemplateRef, NgZone, inject } from '@angular/core';
import { defer, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FieldValue } from '@angular/fire/firestore';
import {
  HealthCheckNotification,
  HealthCheckNotificationStatus,
} from '@shared/models/health-check-notifications';
import { DataObject } from '@shared/generic-types';
import isEqual from 'lodash/isEqual';
import { ConfirmationDialogService } from '../../grid-features/confirmation-dialog/confirmation-dialog.service';
import { UserService } from '../../auth/user.service';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { NbActionsModule, NbTooltipModule, NbCardModule } from '@nebular/theme';
import { CdkLetDirective } from '../cdk-let/cdk-let.directive';
import { HEALTH_CHECK_NOTIFICATIONS_COLLECTION_NAME } from '@shared/constants/health-check-notifications.constants';
import { inAngularZone } from '../../utils/firestore/in-angular-zone';

type DocPartialUpdate<T> = Partial<T> | DataObject<FieldValue>;

export interface UserNotificationContext<T extends HealthCheckNotification = any> {
  notification: T;
  isRead: boolean;
  markAsRead: (notification: HealthCheckNotification, isRead: boolean) => void;
  deleteButtonClicked: (notification: HealthCheckNotification) => void;
}

@Component({
  selector: 'implto-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss'],
  imports: [
    NbActionsModule,
    NbTooltipModule,
    NgClass,
    NbCardModule,
    ButtonModule,
    RouterLink,
    NgTemplateOutlet,
    AsyncPipe,
    CdkLetDirective,
  ],
})
export class UserNotificationsComponent<T extends HealthCheckNotification> {
  private ngZone = inject(NgZone);
  @ContentChild(TemplateRef) templateRef?: TemplateRef<UserNotificationContext>;

  @Input()
  caption = '';

  isVisible = false;
  unreadCnt = 0;

  public readonly notifications$?: Observable<T[]> = defer<Observable<T[]>>(() =>
    this.firestore
      .collection<T>(HEALTH_CHECK_NOTIFICATIONS_COLLECTION_NAME, (ref) => {
        return ref
          .where('status', '!=', HealthCheckNotificationStatus.RESOLVED)
          .where('userRolesToNotify', 'array-contains-any', this.userService.dbUser.roles)
          .orderBy('status', 'asc')
          .orderBy('createdAt', 'desc');
      })
      .valueChanges({ idField: 'id' })
      .pipe(
        inAngularZone(this.ngZone),
        distinctUntilChanged(isEqual),
        map((notifications: T[]) => {
          notifications.sort((a, b) => {
            if (
              a.status === HealthCheckNotificationStatus.NEW &&
              b.status === HealthCheckNotificationStatus.READ
            ) {
              return -1; // 'unread' comes before 'read'
            } else if (
              a.status === HealthCheckNotificationStatus.READ &&
              b.status === HealthCheckNotificationStatus.NEW
            ) {
              return 1; // 'read' comes after 'unread'
            }
            return 0; // no change in order
          });
          this.unreadCnt = notifications.reduce(
            (count, obj) => (obj.status === HealthCheckNotificationStatus.NEW ? count + 1 : count),
            0,
          );
          return notifications;
        }),
      ),
  );

  constructor(
    private firestore: AngularFirestore,
    private confirmationDialogService: ConfirmationDialogService,
    private userService: UserService,
  ) {}

  toggleVisibility(): void {
    this.isVisible = !this.isVisible;
  }

  createContext(notification: HealthCheckNotification): UserNotificationContext {
    return {
      notification: notification,
      isRead: this.isRead(notification),
      markAsRead: this.markAsRead.bind(this),
      deleteButtonClicked: this.deleteButtonClicked.bind(this),
    };
  }

  isAdmin() {
    return this.userService.hasRole('admin');
  }

  private isRead(notification: HealthCheckNotification): boolean {
    return notification.status !== HealthCheckNotificationStatus.NEW;
  }

  private async markAsRead(notification: HealthCheckNotification, isRead: boolean): Promise<void> {
    const doc = this.firestore.doc(
      `${HEALTH_CHECK_NOTIFICATIONS_COLLECTION_NAME}/${notification.id}`,
    );
    const update: DocPartialUpdate<HealthCheckNotification> = {
      status: isRead ? HealthCheckNotificationStatus.READ : HealthCheckNotificationStatus.NEW,
    };
    await doc.update(update);
  }

  private async deleteButtonClicked(notification: HealthCheckNotification): Promise<void> {
    const result = await this.confirmationDialogService.show(
      'Mark notification as resolved?',
      notification.content,
    );
    if (result) {
      const doc = this.firestore.doc(
        `${HEALTH_CHECK_NOTIFICATIONS_COLLECTION_NAME}/${notification.id}`,
      );
      const update: DocPartialUpdate<HealthCheckNotification> = {
        status: HealthCheckNotificationStatus.RESOLVED,
      };
      await doc.update(update);
    }
  }
}
